import { mui } from "@/mui";
import {
  Volume2Icon,
  VideoIcon,
  MonitorUp,
  Languages,
  CircleDot,
  NetworkIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState, useEffect, useCallback } from "react";
import { useMobyNodeClient } from "../provider";
import { useMediaSessions } from "@/hooks/useMediaSessions";
import { StreamQuality } from "@/lib/av-stream-types";
import { toast } from "sonner";

// ─── Preference keys ──────────────────────────────────────────────────────────

const AV_PREF_PREFIX = "moby_av_pref_";

function getAVPreference<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${AV_PREF_PREFIX}${key}`);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function setAVPreference(key: string, value: any): void {
  try {
    localStorage.setItem(`${AV_PREF_PREFIX}${key}`, JSON.stringify(value));
  } catch (error) {
    console.error(`[AV_SETTINGS] Failed to save preference ${key}:`, error);
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

type CompressorPreset = "off" | "light" | "heavy";

interface AVSettings {
  // Audio Processing
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  compressorPreset: CompressorPreset;

  // Video Quality
  resolutionCap: 360 | 720 | 1080;
  qualityOverride: StreamQuality;

  // Screen Share
  screenShareFps: 5 | 15 | 30;

  // Recording (coming soon — stored for future use)
  recordingFormat: "webm" | "mp4";
  recordingResolution: "source" | "720p" | "480p" | "360p";
  recordingAudioSampleRate: 48000 | 16000;
  recordingMerkleSidecar: boolean;
}

const DEFAULT_SETTINGS: AVSettings = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  compressorPreset: "off",
  resolutionCap: 720,
  qualityOverride: StreamQuality.AUTO,
  screenShareFps: 15,
  recordingFormat: "webm",
  recordingResolution: "source",
  recordingAudioSampleRate: 48000,
  recordingMerkleSidecar: true,
};

// ─── Compressor presets ───────────────────────────────────────────────────────

const COMPRESSOR_PRESETS: Record<CompressorPreset, { threshold: number; knee: number; ratio: number; attack: number; release: number } | null> = {
  off: null,
  light: { threshold: -24, knee: 30, ratio: 4, attack: 0.003, release: 0.25 },
  heavy: { threshold: -40, knee: 10, ratio: 12, attack: 0.001, release: 0.1 },
};

// ─── Resolution labels ───────────────────────────────────────────────────────

const RESOLUTION_OPTIONS: { value: 360 | 720 | 1080; label: string; detail: string }[] = [
  { value: 360, label: "360p", detail: "Low bandwidth" },
  { value: 720, label: "720p", detail: "Balanced" },
  { value: 1080, label: "1080p", detail: "High quality" },
];

const QUALITY_OPTIONS: { value: StreamQuality; label: string }[] = [
  { value: StreamQuality.AUTO, label: "Auto" },
  { value: StreamQuality.LOW, label: "Low" },
  { value: StreamQuality.MEDIUM, label: "Med" },
  { value: StreamQuality.HIGH, label: "High" },
];

const FPS_OPTIONS: { value: 5 | 15 | 30; label: string }[] = [
  { value: 5, label: "5 fps" },
  { value: 15, label: "15 fps" },
  { value: 30, label: "30 fps" },
];

// ─── Star Topology Toggle ──────────────────────────────────────────────────────

const STAR_TOPOLOGY_FLAG = 'moby_pref_starTopology';

function StarTopologyToggle() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STAR_TOPOLOGY_FLAG) !== 'false';
    } catch {
      return true;
    }
  });

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(STAR_TOPOLOGY_FLAG, String(next));
    toast.success(next ? "Star topology enabled — takes effect on next group call" : "Star topology disabled — using full mesh");
  };

  return (
    <div className="space-y-2">
      <ToggleRow
        label="Star Topology"
        description="Route group call audio through a hub peer instead of full mesh"
        checked={enabled}
        onChange={toggle}
      />
      <p className="text-body-sm text-on-surface-variant px-3">
        Reduces renegotiation storms in large groups. The most capable peer is
        automatically elected as hub and forwards audio to all others.
      </p>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AVSettingsDialog({ children }: { children: ReactNode }) {
  const node = useMobyNodeClient();
  const sessions = useMediaSessions();
  const [open, setOpen] = useState(false);

  // Local state — loaded from localStorage on open
  const [settings, setSettings] = useState<AVSettings>(DEFAULT_SETTINGS);

  // Load preferences when dialog opens
  useEffect(() => {
    if (!open) return;
    setSettings({
      echoCancellation: getAVPreference("echoCancellation", DEFAULT_SETTINGS.echoCancellation),
      noiseSuppression: getAVPreference("noiseSuppression", DEFAULT_SETTINGS.noiseSuppression),
      autoGainControl: getAVPreference("autoGainControl", DEFAULT_SETTINGS.autoGainControl),
      compressorPreset: getAVPreference("compressorPreset", DEFAULT_SETTINGS.compressorPreset),
      resolutionCap: getAVPreference("resolutionCap", DEFAULT_SETTINGS.resolutionCap),
      qualityOverride: getAVPreference("qualityOverride", DEFAULT_SETTINGS.qualityOverride),
      screenShareFps: getAVPreference("screenShareFps", DEFAULT_SETTINGS.screenShareFps),
      recordingFormat: getAVPreference("recordingFormat", DEFAULT_SETTINGS.recordingFormat),
      recordingResolution: getAVPreference("recordingResolution", DEFAULT_SETTINGS.recordingResolution),
      recordingAudioSampleRate: getAVPreference("recordingAudioSampleRate", DEFAULT_SETTINGS.recordingAudioSampleRate),
      recordingMerkleSidecar: getAVPreference("recordingMerkleSidecar", DEFAULT_SETTINGS.recordingMerkleSidecar),
    });
  }, [open]);

  // ─── Apply live constraints to active session ──────────────────────────────

  // Audio processing (echo, noise, AGC) are creation-time constraints in
  // Chrome/Edge — applyConstraints() silently no-ops. We must re-acquire the
  // mic with new constraints and replaceTrack() on every active sender.
  const applyAudioLive = useCallback(async (s: AVSettings): Promise<boolean> => {
    let applied = false;
    for (const ctx of sessions) {
      const session = ctx.session;
      const oldAudioTrack = session.localStream?.getAudioTracks()[0];
      if (!oldAudioTrack) continue;

      try {
        // Get a fresh mic track with the new processing constraints
        const freshStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: s.echoCancellation,
            noiseSuppression: s.noiseSuppression,
            autoGainControl: s.autoGainControl,
            sampleRate: 48000,
          },
          video: false,
        });
        const newAudioTrack = freshStream.getAudioTracks()[0];
        if (!newAudioTrack) continue;

        // Replace on every audio sender transceiver (no renegotiation needed)
        for (const transceiver of session.localTransceivers) {
          if (transceiver.sender.track?.kind === "audio") {
            await transceiver.sender.replaceTrack(newAudioTrack);
            console.log("[AV_SETTINGS] Replaced audio track on transceiver", {
              mid: transceiver.mid,
            });
          }
        }

        // Swap the track on localStream so the UI stays in sync
        session.localStream?.removeTrack(oldAudioTrack);
        oldAudioTrack.stop();
        session.localStream?.addTrack(newAudioTrack);

        applied = true;
        console.log("[AV_SETTINGS] Audio processing updated live", {
          echo: s.echoCancellation,
          noise: s.noiseSuppression,
          agc: s.autoGainControl,
        });
      } catch (err) {
        console.warn("[AV_SETTINGS] Failed to replace audio track:", err);
      }
    }
    return applied;
  }, [sessions]);

  // Video resolution cap — applyConstraints works for width/height on live
  // video tracks (not audio processing, but actual video dimension constraints).
  const applyVideoResolutionLive = useCallback(async (resCap: 360 | 720 | 1080): Promise<boolean> => {
    const heightMap: Record<number, { w: number; h: number }> = {
      360: { w: 640, h: 360 },
      720: { w: 1280, h: 720 },
      1080: { w: 1920, h: 1080 },
    };
    const { w, h } = heightMap[resCap];
    let applied = false;

    for (const ctx of sessions) {
      const videoTrack = ctx.session.localStream?.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== "live") continue;
      // Skip if this is a screen share track (don't constrain display capture resolution)
      if (ctx.session.screenShareEnabled && ctx.session.screenShareTrack === videoTrack) continue;

      try {
        await videoTrack.applyConstraints({
          width: { max: w },
          height: { max: h },
        });
        applied = true;
        console.log("[AV_SETTINGS] Video resolution cap applied:", resCap);
      } catch (err) {
        console.warn("[AV_SETTINGS] Failed to apply video resolution:", err);
      }
    }
    return applied;
  }, [sessions]);

  // Screen share FPS — applyConstraints works for frameRate on display tracks.
  const applyScreenShareFpsLive = useCallback(async (fps: number): Promise<boolean> => {
    let applied = false;
    for (const ctx of sessions) {
      const screenTrack = ctx.session.screenShareTrack;
      if (!screenTrack || screenTrack.readyState !== "live") continue;
      try {
        await screenTrack.applyConstraints({ frameRate: fps });
        applied = true;
        console.log("[AV_SETTINGS] Screen share FPS applied:", fps);
      } catch (err) {
        console.warn("[AV_SETTINGS] Failed to apply screen share FPS:", err);
      }
    }
    return applied;
  }, [sessions]);

  // ─── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    // Persist all settings to localStorage
    for (const [key, value] of Object.entries(settings)) {
      setAVPreference(key, value);
    }

    const hasActiveSessions = sessions.length > 0;
    const liveApplied: string[] = [];
    const savedForNext: string[] = [];

    if (hasActiveSessions) {
      // Audio processing: getUserMedia + replaceTrack (live)
      const audioOk = await applyAudioLive(settings);
      if (audioOk) liveApplied.push("audio processing");

      // Video resolution: applyConstraints (live)
      const videoOk = await applyVideoResolutionLive(settings.resolutionCap);
      if (videoOk) liveApplied.push("video resolution");

      // Screen share FPS: applyConstraints (live)
      const screenOk = await applyScreenShareFpsLive(settings.screenShareFps);
      if (screenOk) liveApplied.push("screen share FPS");

      // Quality override & compressor: saved for next session / future
      if (settings.compressorPreset !== "off") savedForNext.push("compressor");
      savedForNext.push("quality override");
    }

    // Dispatch event so other components (e.g. provider) can react
    window.dispatchEvent(new CustomEvent("av-settings-updated", { detail: settings }));

    // Informative toast
    if (liveApplied.length > 0) {
      toast.success(`Applied live: ${liveApplied.join(", ")}`);
    } else if (hasActiveSessions) {
      toast.success("Settings saved — applied on next stream");
    } else {
      toast.success("Settings saved");
    }

    setOpen(false);
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const update = <K extends keyof AVSettings>(key: K, value: AVSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <mui.Dialog.Root open={open} onOpenChange={setOpen}>
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop />
        <mui.Dialog.Popup className="max-w-md max-h-[90vh] flex flex-col">
          <mui.Dialog.Title>A/V Settings</mui.Dialog.Title>
          <mui.Dialog.Description>
            Configure audio processing, video quality, and screen share
          </mui.Dialog.Description>

          <div className="space-y-6 mt-6 overflow-y-auto flex-1 px-1">

            {/* ─── Audio Processing ─────────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Volume2Icon size={18} className="text-primary" />
                <span className="text-title-sm font-medium">Audio Processing</span>
              </div>

              <div className="space-y-2">
                <ToggleRow
                  label="Echo Cancellation"
                  description="Reduce echo from speakers"
                  checked={settings.echoCancellation}
                  onChange={(v) => update("echoCancellation", v)}
                />
                <ToggleRow
                  label="Noise Suppression"
                  description="Filter background noise"
                  checked={settings.noiseSuppression}
                  onChange={(v) => update("noiseSuppression", v)}
                />
                <ToggleRow
                  label="Auto-Gain Control"
                  description="Normalize microphone volume"
                  checked={settings.autoGainControl}
                  onChange={(v) => update("autoGainControl", v)}
                />

                {/* Compressor/Limiter preset */}
                <div className="pt-1">
                  <label className="text-body-sm text-on-surface-variant mb-2 block">
                    Compressor / Limiter
                  </label>
                  <div className="flex gap-2">
                    {(["off", "light", "heavy"] as CompressorPreset[]).map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => update("compressorPreset", preset)}
                        className={`flex-1 py-2 rounded-lg border text-label-md font-medium transition capitalize ${
                          settings.compressorPreset === preset
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-outline text-on-surface-variant hover:bg-on-surface-variant/8"
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  {settings.compressorPreset !== "off" && (
                    <p className="text-body-sm text-on-surface-variant mt-1">
                      {settings.compressorPreset === "light"
                        ? "Gentle compression — smooths volume peaks"
                        : "Aggressive limiting — prevents clipping, flattens dynamics"}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* ─── Video Quality ────────────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <VideoIcon size={18} className="text-primary" />
                <span className="text-title-sm font-medium">Video Quality</span>
              </div>

              {/* Resolution cap */}
              <div className="mb-3">
                <label className="text-body-sm text-on-surface-variant mb-2 block">
                  Resolution Cap
                </label>
                <div className="flex gap-2">
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update("resolutionCap", opt.value)}
                      className={`flex-1 py-2 rounded-lg border text-center transition ${
                        settings.resolutionCap === opt.value
                          ? "border-primary bg-primary/10"
                          : "border-outline hover:bg-on-surface-variant/8"
                      }`}
                    >
                      <div className="text-label-md font-medium">{opt.label}</div>
                      <div className="text-body-sm text-on-surface-variant">{opt.detail}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quality override */}
              <div>
                <label className="text-body-sm text-on-surface-variant mb-2 block">
                  Quality Override
                </label>
                <div className="flex gap-2">
                  {QUALITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update("qualityOverride", opt.value)}
                      className={`flex-1 py-2 rounded-lg border text-label-md font-medium transition ${
                        settings.qualityOverride === opt.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-outline text-on-surface-variant hover:bg-on-surface-variant/8"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* ─── Screen Share ─────────────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MonitorUp size={18} className="text-primary" />
                <span className="text-title-sm font-medium">Screen Share</span>
              </div>

              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Frame Rate
              </label>
              <div className="flex gap-2">
                {FPS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => update("screenShareFps", opt.value)}
                    className={`flex-1 py-2 rounded-lg border text-label-md font-medium transition ${
                      settings.screenShareFps === opt.value
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-outline text-on-surface-variant hover:bg-on-surface-variant/8"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-body-sm text-on-surface-variant mt-1">
                Higher FPS uses more bandwidth. 15 fps is recommended for most content.
              </p>
            </section>

            {/* ─── Network / Topology ──────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <NetworkIcon size={18} className="text-primary" />
                <span className="text-title-sm font-medium">Network</span>
              </div>

              <StarTopologyToggle />
            </section>

            {/* ─── Translator (coming soon) ─────────────────────────── */}
            <section className="opacity-50">
              <div className="flex items-center gap-2 mb-3">
                <Languages size={18} className="text-on-surface-variant" />
                <span className="text-title-sm font-medium">Translator</span>
                <span className="text-body-sm bg-surface-container px-2 py-0.5 rounded-full">Coming Soon</span>
              </div>
              <p className="text-body-sm text-on-surface-variant">
                Real-time speech-to-text translation with letterbox-style subtitles overlay.
              </p>
            </section>

            {/* ─── Recording (coming soon) ──────────────────────────── */}
            <section className="opacity-50">
              <div className="flex items-center gap-2 mb-3">
                <CircleDot size={18} className="text-on-surface-variant" />
                <span className="text-title-sm font-medium">Recording</span>
                <span className="text-body-sm bg-surface-container px-2 py-0.5 rounded-full">Coming Soon</span>
              </div>

              <div className="space-y-3 pointer-events-none">
                {/* Format */}
                <div>
                  <label className="text-body-sm text-on-surface-variant mb-1 block">Format</label>
                  <div className="flex gap-2">
                    {(["webm", "mp4"] as const).map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        disabled
                        className={`flex-1 py-2 rounded-lg border text-label-md font-medium uppercase ${
                          settings.recordingFormat === fmt
                            ? "border-primary/50 bg-primary/5"
                            : "border-outline"
                        }`}
                      >
                        {fmt}
                        {fmt === "mp4" && <span className="text-body-sm normal-case ml-1">(beta)</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Output resolution */}
                <div>
                  <label className="text-body-sm text-on-surface-variant mb-1 block">Output Resolution</label>
                  <div className="flex gap-2">
                    {(["source", "720p", "480p", "360p"] as const).map((res) => (
                      <button
                        key={res}
                        type="button"
                        disabled
                        className={`flex-1 py-1.5 rounded-lg border text-label-md font-medium capitalize ${
                          settings.recordingResolution === res
                            ? "border-primary/50 bg-primary/5"
                            : "border-outline"
                        }`}
                      >
                        {res === "source" ? "Source" : res}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Audio sample rate */}
                <div>
                  <label className="text-body-sm text-on-surface-variant mb-1 block">Audio Sample Rate</label>
                  <div className="flex gap-2">
                    {([48000, 16000] as const).map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        disabled
                        className={`flex-1 py-2 rounded-lg border text-label-md font-medium ${
                          settings.recordingAudioSampleRate === rate
                            ? "border-primary/50 bg-primary/5"
                            : "border-outline"
                        }`}
                      >
                        {rate === 48000 ? "48 kHz (Full)" : "16 kHz (Voice)"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Merkle sidecar */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-outline">
                  <div>
                    <div className="text-body-md font-medium">Merkle Tree Fingerprint</div>
                    <div className="text-body-sm text-on-surface-variant">
                      Cryptographic receipt embedded in video header + JSON sidecar
                    </div>
                  </div>
                  <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${
                      settings.recordingMerkleSidecar ? "bg-primary/50" : "bg-on-surface-variant/30"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        settings.recordingMerkleSidecar ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </section>

          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-6">
            <mui.Button onClick={() => setOpen(false)} variant="surface">
              Cancel
            </mui.Button>
            <mui.Button onClick={handleSave} variant="primary" filled>
              Save
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}

// ─── Toggle Row sub-component ─────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between p-3 rounded-lg border border-outline hover:bg-on-surface-variant/8 transition"
    >
      <div className="text-left">
        <div className="text-body-md font-medium">{label}</div>
        <div className="text-body-sm text-on-surface-variant">{description}</div>
      </div>
      <div
        className={`w-10 h-6 rounded-full relative transition-colors flex-shrink-0 ml-3 ${
          checked ? "bg-primary" : "bg-on-surface-variant/30"
        }`}
      >
        <div
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </div>
    </button>
  );
}
